import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gql, useApolloClient } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import { useS3UploadAsync } from "@src/features/app/hooks/useS3Upload";
import {
  OpusAgentAttachmentInput,
  OpusAgentAttachmentPhase,
  OpusAgentAttachmentState,
} from "@src/types.generated";
import {
  OpusAgentAttachmentLibraryItemFragment,
  OpusAgentAttachmentStatusFieldsFragment,
  OpusAgentAttachmentStatusesDocument,
  OpusAgentAttachmentStatusesQuery,
  OpusAgentAttachmentStatusesQueryVariables,
  useOpusAgentAttachmentCreateMediaMutation,
  useStartOpusAgentAttachmentsMutation,
} from "./useOpusAgentAttachments.generated";
import {
  OpusAgentAttachmentPill,
  OpusAgentSendAttachment,
  OpusAgentSendGate,
  attachmentInput,
  attachmentSendGate,
  canRetryAttachment,
} from "./sendGate";

export const ATTACHMENT_POLL_INTERVAL_MS = 1500;

export interface UseOpusAgentAttachmentsReturn {
  pills: OpusAgentAttachmentPill[];
  gate: OpusAgentSendGate;
  // MAX_OPUS_AGENT_ATTACHMENTS reached: attaching more is disabled.
  atLimit: boolean;
  // Returns how many files did not fit under the limit.
  addFiles: (files: File[]) => number;
  addLibraryItem: (libraryItem: OpusAgentAttachmentLibraryItemFragment) => void;
  remove: (key: string) => void;
  // Tries a failed pill again: re-uploads its file, or asks the server to
  // read its Media or library item again.
  retry: (key: string) => void;
  clear: () => void;
  // The ready pills' server objects in pill order, for sendMessage.
  sendable: () => OpusAgentSendAttachment[];
}

const matchesInput = (
  status: OpusAgentAttachmentStatusFieldsFragment,
  input: OpusAgentAttachmentInput,
): boolean =>
  input.mediaId != null
    ? status.mediaId === input.mediaId
    : input.libraryItemId != null &&
      status.libraryItemId === input.libraryItemId;

const pillPatchForStatus = (
  status: OpusAgentAttachmentStatusFieldsFragment,
): Partial<OpusAgentAttachmentPill> => ({
  state:
    status.state === OpusAgentAttachmentState.Ready
      ? "ready"
      : status.state === OpusAgentAttachmentState.Failed
        ? "failed"
        : "processing",
  detail: status.detail ?? null,
  status,
});

// Composer attachments for the Opus Agent: uploads files (S3 presigned
// upload + createMedia, as the outline chat did), asks the server to start
// reading each attachment the moment it is added, then polls readiness for
// every pill still processing until it is READY or FAILED.
// The server's per-message limit (MAX_ATTACHMENTS in
// opus_agent/controllers/attachments.py): past it, start/poll/send refuse.
export const MAX_OPUS_AGENT_ATTACHMENTS = 10;

// The server reads files up to 50 MB (MAX_ATTACHMENT_BYTES) of these types
// (opus_agent/media_kinds.py); anything else is refused before it uploads.
export const MAX_OPUS_AGENT_ATTACHMENT_BYTES = 50_000_000;
export const OPUS_AGENT_ATTACHMENT_EXTENSIONS = [
  ".pdf",
  ".docx",
  ".pptx",
  ".xlsx",
  ".csv",
  ".txt",
  ".html",
  ".htm",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
];

// Legacy Excel isn't read (its reader has no memory bound; the server
// refuses it too, REFUSED_EXTENSIONS in opus_agent/media_kinds.py): say how
// to attach it instead.
export const LEGACY_EXCEL_REFUSAL = "Save .xls files as .xlsx to attach them.";

// Why a picked file can't be attached, or null when it can.
export const attachmentRefusal = (file: File): string | null => {
  const name = file.name.toLowerCase();
  if (name.endsWith(".xls")) return LEGACY_EXCEL_REFUSAL;
  if (!OPUS_AGENT_ATTACHMENT_EXTENSIONS.some((ext) => name.endsWith(ext)))
    return "Unsupported file type";
  if (file.size > MAX_OPUS_AGENT_ATTACHMENT_BYTES)
    return `Over ${MAX_OPUS_AGENT_ATTACHMENT_BYTES / 1_000_000} MB`;
  return null;
};

const needsPoll = (pill: OpusAgentAttachmentPill): boolean =>
  (pill.state === "processing" && pill.started) ||
  (pill.state === "ready" &&
    pill.status?.phase === OpusAgentAttachmentPhase.ReadingImages);

// Consecutive failed status polls before the pending pills are given up on:
// the server refuses the whole batch (flag off, a bad ref), so retrying
// forever would leave send blocked with no reason shown.
const MAX_POLL_FAILURES = 5;

// ``enabled`` off: the hook holds no pills, polls nothing and never blocks
// send.
export const useOpusAgentAttachments = (
  enabled = true,
): UseOpusAgentAttachmentsReturn => {
  const client = useApolloClient();
  const [pills, setPills] = useState<OpusAgentAttachmentPill[]>([]);
  const pillsRef = useRef(pills);
  pillsRef.current = pills;

  const [createMedia] = useOpusAgentAttachmentCreateMediaMutation();
  const [startAttachments] = useStartOpusAgentAttachmentsMutation();
  // The upload's file id is the pill key (handleFileUpload(file, key)).
  // Kept to steps of 10: each step re-renders the whole chat.
  const onUploadProgress = useCallback((key: string, rawPercent: number) => {
    const percent = Math.floor(rawPercent / 10) * 10;
    setPills((prev) =>
      prev.some(
        (pill) =>
          pill.key === key &&
          pill.state === "uploading" &&
          pill.uploadPercent !== percent,
      )
        ? prev.map((pill) =>
            pill.key === key ? { ...pill, uploadPercent: percent } : pill,
          )
        : prev,
    );
  }, []);
  const { handleFileUpload } = useS3UploadAsync({ onUploadProgress });

  // Patches only a pill still present: a removed pill's late result is dropped.
  const patchPill = useCallback(
    (key: string, patch: Partial<OpusAgentAttachmentPill>) =>
      setPills((prev) =>
        prev.map((pill) => (pill.key === key ? { ...pill, ...patch } : pill)),
      ),
    [],
  );

  const start = useCallback(
    async (key: string, input: OpusAgentAttachmentInput) => {
      try {
        const { data } = await startAttachments({
          variables: { input: [input] },
        });
        const result = data?.startOpusAgentAttachments;
        if (!result?.success) {
          patchPill(key, {
            state: "failed",
            started: true,
            detail: result?.error?.message || "Couldn't read this attachment",
          });
          return;
        }
        const status = result.statuses?.find((s) => matchesInput(s, input));
        patchPill(key, {
          started: true,
          ...(status ? pillPatchForStatus(status) : { state: "processing" }),
        });
      } catch (error) {
        patchPill(key, {
          state: "failed",
          started: true,
          detail:
            error instanceof Error && error.message
              ? error.message
              : "Couldn't read this attachment",
        });
      }
    },
    [startAttachments, patchPill],
  );

  // Unmounted (the chat switched, or a kill switch replaced it): uploads
  // still in flight stop at their next step.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // A pill removed mid-upload, or a hook unmounted mid-upload, stops there:
  // no Media, no server read.
  const isPresent = useCallback(
    (key: string) =>
      mountedRef.current && pillsRef.current.some((pill) => pill.key === key),
    [],
  );

  const uploadFile = useCallback(
    async (key: string, file: File) => {
      try {
        const upload = await handleFileUpload(file, key);
        if (!isPresent(key)) return;
        if (!upload) {
          patchPill(key, { state: "failed", detail: "Upload failed" });
          return;
        }
        const { data } = await createMedia({
          variables: { input: { mediaUrls: { en: upload.url } } },
        });
        const media = data?.createMedia?.media;
        if (!data?.createMedia?.success || !media) {
          patchPill(key, {
            state: "failed",
            detail: data?.createMedia?.error?.message || "Upload failed",
          });
          return;
        }
        if (!isPresent(key)) return;
        patchPill(key, { media, state: "processing", detail: null });
        await start(key, { mediaId: media.id });
      } catch (error) {
        patchPill(key, {
          state: "failed",
          detail: error instanceof Error ? error.message : "Upload failed",
        });
      }
    },
    [handleFileUpload, createMedia, patchPill, start, isPresent],
  );

  const addFiles = useCallback(
    (files: File[]): number => {
      const room = Math.max(
        MAX_OPUS_AGENT_ATTACHMENTS - pillsRef.current.length,
        0,
      );
      const added = files
        .slice(0, room)
        .map((file) => ({ key: uuidv4(), file }));
      const skipped = files.length - added.length;
      if (added.length === 0) return skipped;
      // A file the server would refuse is shown failed, with why, and never
      // uploads.
      const refusals = new Map(
        added.map(({ key, file }) => [key, attachmentRefusal(file)]),
      );
      setPills((prev) => [
        ...prev,
        ...added.map(({ key, file }): OpusAgentAttachmentPill => {
          const refusal = refusals.get(key);
          return refusal
            ? { key, state: "failed", fileName: file.name, detail: refusal }
            : { key, state: "uploading", fileName: file.name, file };
        }),
      ]);
      // The ref is read by isPresent before React re-renders.
      pillsRef.current = [
        ...pillsRef.current,
        ...added.map(({ key }) => ({
          key,
          state: refusals.get(key)
            ? ("failed" as const)
            : ("uploading" as const),
        })),
      ];
      added.forEach(({ key, file }) => {
        if (!refusals.get(key)) void uploadFile(key, file);
      });
      return skipped;
    },
    [uploadFile],
  );

  const addLibraryItem = useCallback(
    (libraryItem: OpusAgentAttachmentLibraryItemFragment) => {
      if (
        pillsRef.current.length >= MAX_OPUS_AGENT_ATTACHMENTS ||
        pillsRef.current.some((pill) => pill.libraryItem?.id === libraryItem.id)
      )
        return;
      const key = uuidv4();
      pillsRef.current = [
        ...pillsRef.current,
        { key, state: "processing", libraryItem },
      ];
      setPills((prev) => [...prev, { key, state: "processing", libraryItem }]);
      void start(key, { libraryItemId: libraryItem.id });
    },
    [start],
  );

  const remove = useCallback(
    (key: string) =>
      setPills((prev) => prev.filter((pill) => pill.key !== key)),
    [],
  );
  const retry = useCallback(
    (key: string) => {
      const pill = pillsRef.current.find((p) => p.key === key);
      if (!pill || !canRetryAttachment(pill)) return;
      const input = attachmentInput(pill);
      const patch: Partial<OpusAgentAttachmentPill> = input
        ? { state: "processing", started: false, detail: null, status: null }
        : {
            state: "uploading",
            detail: null,
            status: null,
            uploadPercent: null,
          };
      // Updated before React re-renders, so a second click is a no-op.
      pillsRef.current = pillsRef.current.map((p) =>
        p.key === key ? { ...p, ...patch } : p,
      );
      patchPill(key, patch);
      if (input) void start(key, input);
      else if (pill.file) void uploadFile(key, pill.file);
    },
    [patchPill, start, uploadFile],
  );
  const clear = useCallback(() => setPills([]), []);

  const sendable = useCallback(
    () =>
      pillsRef.current
        .filter((pill) => pill.state === "ready")
        .map(({ media, libraryItem }) => ({ media, libraryItem })),
    [],
  );

  // Poll while any started pill is still processing, or is ready while its
  // pictures are still being read (so its pill keeps counting); the interval
  // stops when none is left and on unmount. One request in flight at a time.
  const hasPending = pills.some(needsPoll);
  useEffect(() => {
    if (!hasPending || !enabled) return;
    let cancelled = false;
    let inFlight = false;
    let failures = 0;
    // Failures count per batch: a pill added later starts from zero.
    let batch = "";
    const poll = async () => {
      if (inFlight) return;
      const pending = pillsRef.current
        .filter(needsPoll)
        .map((pill) => ({ key: pill.key, input: attachmentInput(pill) }))
        .filter(
          (entry): entry is { key: string; input: OpusAgentAttachmentInput } =>
            entry.input !== null,
        );
      if (pending.length === 0) return;
      const batchKey = pending.map((entry) => entry.key).join(",");
      if (batchKey !== batch) {
        batch = batchKey;
        failures = 0;
      }
      inFlight = true;
      try {
        const { data } = await client.query<
          OpusAgentAttachmentStatusesQuery,
          OpusAgentAttachmentStatusesQueryVariables
        >({
          query: OpusAgentAttachmentStatusesDocument,
          variables: { input: pending.map((entry) => entry.input) },
          fetchPolicy: "no-cache",
        });
        if (cancelled) return;
        const statuses = data?.OpusAgentAttachmentStatuses ?? [];
        // A reply that leaves a pending pill out counts as a failed read.
        const answered = pending.every((entry) =>
          statuses.some((s) => matchesInput(s, entry.input)),
        );
        if (!answered) throw new Error("Couldn't check this attachment");
        failures = 0;
        setPills((prev) =>
          prev.map((pill) => {
            const entry = pending.find((p) => p.key === pill.key);
            if (!entry || !needsPoll(pill)) return pill;
            const status = statuses.find((s) => matchesInput(s, entry.input));
            return status ? { ...pill, ...pillPatchForStatus(status) } : pill;
          }),
        );
      } catch (error) {
        // A failed read is retried on the next tick, a few times.
        failures += 1;
        if (!cancelled && failures >= MAX_POLL_FAILURES) {
          const keys = new Set(pending.map((entry) => entry.key));
          const detail =
            error instanceof Error && error.message
              ? error.message
              : "Couldn't check this attachment";
          // A pill already sendable stays sendable; it just stops counting
          // its pictures, so the poll can stop.
          setPills((prev) =>
            prev.map((pill) =>
              !keys.has(pill.key)
                ? pill
                : pill.state === "processing"
                  ? { ...pill, state: "failed", detail }
                  : pill.state === "ready" && pill.status
                    ? {
                        ...pill,
                        detail: null,
                        status: {
                          ...pill.status,
                          phase: OpusAgentAttachmentPhase.Ready,
                        },
                      }
                    : pill,
            ),
          );
        }
      } finally {
        inFlight = false;
      }
    };
    const interval = setInterval(() => {
      void poll();
    }, ATTACHMENT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [hasPending, enabled, client]);

  // Switched off mid-session: drop what was attached (the server now refuses
  // it) rather than keep hidden pills that block or ride along on send.
  useEffect(() => {
    if (!enabled) setPills([]);
  }, [enabled]);

  const gate = useMemo(
    () => attachmentSendGate(enabled ? pills : []),
    [enabled, pills],
  );
  const atLimit = pills.length >= MAX_OPUS_AGENT_ATTACHMENTS;

  return {
    pills,
    gate,
    atLimit,
    addFiles,
    addLibraryItem,
    remove,
    retry,
    clear,
    sendable,
  };
};

gql`
  fragment OpusAgentAttachmentMedia on Media {
    id
    thumbnailImageUrl
    mediaUrls {
      uuid
      en
    }
    imageUrls {
      id
      original
      thumb
    }
  }

  fragment OpusAgentAttachment_TrainingResourceVersion on TrainingResourceVersion {
    id
    type
    media {
      ...OpusAgentAttachmentMedia
    }
  }

  fragment OpusAgentAttachmentLibraryItem on LibraryItem {
    id
    type
    name {
      uuid
      en
    }
    coverImage {
      id
      emojiIcon
      background
      imageUrls {
        id
        original
        thumb
      }
    }
    trainingResource {
      id
      draftVersion {
        ...OpusAgentAttachment_TrainingResourceVersion
      }
      publishedVersion {
        ...OpusAgentAttachment_TrainingResourceVersion
      }
    }
  }

  fragment OpusAgentTurnAttachmentFields on OpusAgentTurnAttachment {
    id
    index
    media {
      ...OpusAgentAttachmentMedia
    }
    libraryItem {
      ...OpusAgentAttachmentLibraryItem
    }
  }

  fragment OpusAgentAttachmentStatusFields on OpusAgentAttachmentStatus {
    mediaId
    libraryItemId
    state
    detail
    phase
    progressDone
    progressTotal
    pageCount
    textFound
  }

  mutation OpusAgentAttachmentCreateMedia($input: MediaInput!) {
    createMedia(input: $input) {
      success
      error {
        code
        message
      }
      media {
        ...OpusAgentAttachmentMedia
      }
    }
  }

  mutation StartOpusAgentAttachments($input: [OpusAgentAttachmentInput!]!) {
    startOpusAgentAttachments(input: $input) {
      success
      error {
        code
        message
      }
      statuses {
        ...OpusAgentAttachmentStatusFields
      }
    }
  }

  query OpusAgentAttachmentStatuses($input: [OpusAgentAttachmentInput!]!) {
    OpusAgentAttachmentStatuses(input: $input) {
      ...OpusAgentAttachmentStatusFields
    }
  }
`;
